import React, { useState } from "react";

const getColor = (index, total, confidence) => {
  const percent = (index + 1) / total;
  if (percent <= confidence) {
    if (percent <= 0.33) return "#e53935"; // red for low confidence
    if (percent <= 0.66) return "#ffb300"; // yellow for medium confidence  
    return "#4caf50"; // green for high confidence
  }
  return "#333"; // background/empty (darker for better contrast)
};

const getAIColor = (index, total, confidence) => {
  const percent = (index + 1) / total;
  if (percent <= confidence) {
    if (percent <= 0.33) return "#ff5722"; // orange-red for low AI confidence
    if (percent <= 0.66) return "#ff9800"; // orange for medium AI confidence  
    return "#2196f3"; // blue for high AI confidence
  }
  return "#333"; // background/empty
};

// Regular confidence bar
export default function ConfidenceBar({ confidence = 0.65, segments = 30, height = 16 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
      {Array.from({ length: segments }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 6,
            height,
            borderRadius: 2,
            background: getColor(i, segments, confidence),
            transition: "background 0.2s"
          }}
        />
      ))}
      <span style={{ marginLeft: 8, fontWeight: "bold", color: "#fff", fontSize: 12 }}>
        {(confidence * 100).toFixed(1)}%
      </span>
    </div>
  );
}

// Dual confidence bar showing both signal and AI confidence
export function DualConfidenceBar({ 
  signalConfidence = 0.5, 
  aiConfidence = 0.5, 
  segments = 15, 
  height = 8 
}) {
  return (
    <div style={{ 
      display: "flex", 
      flexDirection: "column",
      alignItems: "center", 
      gap: 2,
      minWidth: 80
    }}>
      {/* Signal Confidence Bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
        <span style={{ fontSize: 8, color: "#ccc", minWidth: 15 }}>SIG</span>
        <div style={{ display: "flex", gap: 1 }}>
          {Array.from({ length: segments }).map((_, i) => (
            <div
              key={i}
              style={{
                width: 3,
                height,
                borderRadius: 1,
                background: getColor(i, segments, signalConfidence),
                transition: "background 0.2s"
              }}
            />
          ))}
        </div>
        <span style={{ fontSize: 8, color: "#fff", minWidth: 25 }}>
          {(signalConfidence * 100).toFixed(0)}%
        </span>
      </div>
      
      {/* AI Confidence Bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
        <span style={{ fontSize: 8, color: "#ccc", minWidth: 15 }}>AI</span>
        <div style={{ display: "flex", gap: 1 }}>
          {Array.from({ length: segments }).map((_, i) => (
            <div
              key={i}
              style={{
                width: 3,
                height,
                borderRadius: 1,
                background: getAIColor(i, segments, aiConfidence),
                transition: "background 0.2s"
              }}
            />
          ))}
        </div>
        <span style={{ fontSize: 8, color: "#fff", minWidth: 25 }}>
          {(aiConfidence * 100).toFixed(0)}%
        </span>
      </div>
    </div>
  );
}

// Compact version for comparison views
export function CompactConfidenceBar({ confidence = 0.65, asset = "", segments = 20, height = 12 }) {
  return (
    <div style={{ 
      display: "flex", 
      alignItems: "center", 
      gap: 4, 
      padding: "2px 0",
      minHeight: 20
    }}>
      <div style={{ 
        minWidth: 60, 
        fontSize: 10, 
        color: "#ccc", 
        fontWeight: "bold",
        textAlign: "left"
      }}>
        {asset}
      </div>
      <div style={{ display: "flex", gap: 1 }}>
        {Array.from({ length: segments }).map((_, i) => (
          <div
            key={i}
            style={{
              width: 4,
              height,
              borderRadius: 1,
              background: getColor(i, segments, confidence),
              transition: "background 0.2s"
            }}
          />
        ))}
      </div>
      <span style={{ 
        marginLeft: 6, 
        fontWeight: "bold", 
        color: "#fff", 
        fontSize: 10,
        minWidth: 35,
        textAlign: "right"
      }}>
        {(confidence * 100).toFixed(0)}%
      </span>
    </div>
  );
}

// Table-optimized confidence bar (most compact)
export function TableConfidenceBar({ confidence = 0.65, segments = 15, height = 10 }) {
  return (
    <div style={{ 
      display: "flex", 
      alignItems: "center", 
      gap: 3,
      justifyContent: "center",
      minHeight: 16
    }}>
      <div style={{ display: "flex", gap: 1 }}>
        {Array.from({ length: segments }).map((_, i) => (
          <div
            key={i}
            style={{
              width: 3,
              height,
              borderRadius: 1,
              background: getColor(i, segments, confidence),
              transition: "background 0.2s"
            }}
          />
        ))}
      </div>
      <span style={{ 
        marginLeft: 3, 
        fontWeight: "bold", 
        color: "#fff", 
        fontSize: 9,
        minWidth: 25,
        textAlign: "left"
      }}>
        {(confidence * 100).toFixed(0)}%
      </span>
    </div>
  );
}

// Horizontal comparison view
export function ConfidenceComparison({ signalData = {} }) {
  const sortedAssets = Object.entries(signalData)
    .sort(([,a], [,b]) => (b.confidence || 0) - (a.confidence || 0))
    .slice(0, 8); // Show top 8 for space

  if (sortedAssets.length === 0) {
    return (
      <div style={{
        background: "#2a2a2a",
        borderRadius: 8,
        padding: 12,
        margin: "10px 0",
        textAlign: "center"
      }}>
        <span style={{ color: "#888", fontSize: 12 }}>
          📊 Loading confidence data...
        </span>
      </div>
    );
  }

  return (
    <div style={{
      background: "#2a2a2a",
      borderRadius: 8,
      padding: 12,
      margin: "10px 0"
    }}>
      <h4 style={{ 
        color: "#fff", 
        margin: "0 0 10px 0", 
        fontSize: 14,
        fontWeight: "bold"
      }}>
        📊 Signal Confidence Ranking
      </h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {sortedAssets.map(([asset, signal]) => (
          <CompactConfidenceBar
            key={asset}
            asset={asset}
            confidence={signal.confidence || 0.5}
          />
        ))}
      </div>
    </div>
  );
}

// Floating confidence panel
export function FloatingConfidencePanel({ signalData = {}, onClose }) {
  const [isMinimized, setIsMinimized] = useState(false);
  
  const sortedAssets = Object.entries(signalData)
    .sort(([,a], [,b]) => (b.confidence || 0) - (a.confidence || 0));

  if (isMinimized) {
    return (
      <div style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        background: "#2a2a2a",
        borderRadius: 25,
        padding: "8px 12px",
        cursor: "pointer",
        border: "2px solid #444",
        zIndex: 1000
      }} onClick={() => setIsMinimized(false)}>
        <span style={{ color: "#fff", fontSize: 12, fontWeight: "bold" }}>
          📊 {sortedAssets.length}
        </span>
      </div>
    );
  }

  return (
    <div style={{
      position: "fixed",
      bottom: 20,
      right: 20,
      background: "#2a2a2a",
      borderRadius: 8,
      padding: 12,
      border: "2px solid #444",
      maxHeight: "400px",
      overflowY: "auto",
      minWidth: "200px",
      zIndex: 1000,
      boxShadow: "0 4px 20px rgba(0,0,0,0.5)"
    }}>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: 10 
      }}>
        <h4 style={{ 
          color: "#fff", 
          margin: 0, 
          fontSize: 12,
          fontWeight: "bold"
        }}>
          📊 Confidence Monitor
        </h4>
        <div style={{ display: "flex", gap: 5 }}>
          <button 
            onClick={() => setIsMinimized(true)}
            style={{
              background: "none",
              border: "none",
              color: "#888",
              cursor: "pointer",
              fontSize: 14
            }}
          >
            _
          </button>
          <button 
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#888",
              cursor: "pointer",
              fontSize: 14
            }}
          >
            ×
          </button>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {sortedAssets.slice(0, 10).map(([asset, signal]) => (
          <CompactConfidenceBar
            key={asset}
            asset={asset}
            confidence={signal.confidence || 0.5}
            segments={12}
            height={8}
          />
        ))}
      </div>
    </div>
  );
} 